import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropdown", "hiddenInput"]
  static values = { searchUrl: String }

  connect() {
    this.debounceTimer = null
    this.selectedIndex = -1
    this.results = []
    
    // Hide dropdown when clicking outside
    document.addEventListener('click', this.hideDropdown.bind(this))
  }

  disconnect() {
    document.removeEventListener('click', this.hideDropdown.bind(this))
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }

  search(event) {
    const query = event.target.value.trim()
    
    // Handle keyboard navigation
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      this.navigateDown()
      return
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      this.navigateUp()
      return
    } else if (event.key === 'Enter') {
      event.preventDefault()
      if (this.selectedIndex >= 0) {
        this.selectCurrent()
      }
      return
    } else if (event.key === 'Escape') {
      this.hideDropdown()
      this.inputTarget.blur()
      return
    }

    // Clear previous timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    // Hide dropdown if query is too short
    if (query.length < 2) {
      this.hideDropdown()
      this.clearHiddenInput()
      return
    }

    // Debounced search
    this.debounceTimer = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  async performSearch(query) {
    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      url.searchParams.append('q', query)
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`)
      }
      
      const data = await response.json()
      this.displayResults(data)
      
    } catch (error) {
      console.error('Hollander search error:', error)
      this.showError('Search unavailable')
    }
  }

  displayResults(results) {
    this.results = results
    this.selectedIndex = results.length > 0 ? 0 : -1  // Auto-select first result
    
    const dropdown = this.dropdownTarget
    
    if (results.length === 0) {
      dropdown.innerHTML = '<div class="hollander-empty">No parts found</div>'
    } else {
      dropdown.innerHTML = results.map((result, index) => `
        <div class="hollander-result ${index === this.selectedIndex ? 'selected' : ''}" 
             data-index="${index}" 
             data-hollander="${result.hollander}"
             data-action="click->hollander-search#select">
          <span class="hollander-code">${result.hollander}</span>
          <span class="hollander-description">${result.plain_english_name}</span>
        </div>
      `).join('')
    }
    
    this.showDropdown()
  }

  showError(message) {
    const dropdown = this.dropdownTarget
    dropdown.innerHTML = `<div class="hollander-empty">⚠️ ${message}</div>`
    this.showDropdown()
  }

  select(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    if (this.results[index]) {
      this.selectResult(this.results[index])
    }
  }

  selectResult(result) {
    // Update the visible input with the description
    this.inputTarget.value = result.plain_english_name
    
    // Update the hidden input with the Hollander code for form submission
    this.hiddenInputTarget.value = result.hollander
    
    this.hideDropdown()
    
    // Trigger change event
    this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    this.hiddenInputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    
    // AUTO-FILTER: Automatically submit the form when a selection is made
    this.autoSubmitForm()
  }
  
  autoSubmitForm() {
    // Find the parent form and submit it automatically
    const form = this.element.closest('form')
    if (form && this.hiddenInputTarget.value) {
      // Small delay to ensure all values are set
      setTimeout(() => {
        // Get the searches controller and trigger submit
        const searchesController = form.controller
        if (searchesController && searchesController.submit) {
          searchesController.submit()
        } else {
          // Fallback: construct URL and navigate
          const baseUrl = form.dataset.url
          const hollanderCode = this.hiddenInputTarget.value
          window.location.href = `${baseUrl}?query=${hollanderCode}`
        }
      }, 100)
    }
  }

  selectCurrent() {
    if (this.selectedIndex >= 0 && this.results[this.selectedIndex]) {
      this.selectResult(this.results[this.selectedIndex])
    }
  }

  navigateDown() {
    if (this.selectedIndex < this.results.length - 1) {
      this.selectedIndex++
      this.updateSelection()
    }
  }

  navigateUp() {
    if (this.selectedIndex > 0) {
      this.selectedIndex--
      this.updateSelection()
    } else if (this.selectedIndex === 0) {
      // Allow unselecting when at the first item
      this.selectedIndex = -1
      this.updateSelection()
    }
  }

  updateSelection() {
    const items = this.dropdownTarget.querySelectorAll('.hollander-result')
    items.forEach((item, index) => {
      item.classList.toggle('selected', index === this.selectedIndex)
    })
    
    // Scroll selected item into view
    const selected = items[this.selectedIndex]
    if (selected) {
      selected.scrollIntoView({ block: 'nearest' })
    }
  }

  showDropdown() {
    this.dropdownTarget.style.display = 'block'
  }

  hideDropdown(event) {
    // Don't hide if clicking within our component
    if (event && this.element.contains(event.target)) {
      return
    }
    
    this.dropdownTarget.style.display = 'none'
    this.selectedIndex = -1
  }

  clearHiddenInput() {
    if (this.hiddenInputTarget.value && !this.inputTarget.value) {
      this.hiddenInputTarget.value = ''
    }
  }

  clear() {
    this.inputTarget.value = ''
    this.hiddenInputTarget.value = ''
    this.hideDropdown()
    this.inputTarget.focus()
  }
}